import { listFeriados, createFeriado, deleteFeriado } from '../infrastructure/feriados.repository.js';

/**
 * Servicio de feriados (Unit S6, design.md AD8, spec `payroll-holidays-config`).
 * Feriados es la ÚNICA tabla de este slice con lectura Y escritura
 * browser-directo bajo RLS (`feriados_admin_all`, migración 20) — a
 * diferencia de `adelantos`/`pagos`, que sólo escriben vía
 * `api/payroll-movimientos.js`. Esta capa orquesta el repositorio ya
 * existente (`feriados.repository.js`, sin cambios) y traduce sus `throw`
 * en el mismo shape `{ ok, kind, mensaje }` que el resto de los servicios de
 * este slice (`admin-adelantos.service.js`) — nunca un código crudo de
 * Postgres llega a la UI.
 */

/** Violación de PK (`fecha` duplicada) — `feriados.repository.js#createFeriado`'s JSDoc. */
const DUPLICATE_KEY = '23505';

/**
 * Copia genérica compartida por esta capa Y por `js/admin.js`'s área de
 * Configuración (Unit S6) — review-1fa036d7d40a06c8 (finding 4): `js/admin.js`
 * minteaba una copia literal duplicada en vez de importar esto. Única fuente.
 */
export const GENERIC_ERROR_MESSAGE = 'Ocurrió un error. Probá de nuevo.';

const ERROR_MESSAGES = {
    fecha_invalida: 'La fecha no es válida.',
    descripcion_requerida: 'Ingresá una descripción para el feriado.',
    ya_existe: 'Ya hay un feriado cargado para ese día.',
    error_interno: GENERIC_ERROR_MESSAGE,
};

const FECHA_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * @param {*} fecha
 * @returns {boolean} `true` si es una fecha calendario real (no sólo el
 * formato) — mismo criterio que `liquidacion-periodo-policy.js`'s
 * round-trip a través de `Date.UTC`, para rechazar `'2026-13-40'`.
 */
function esFechaValida(fecha) {
    if (typeof fecha !== 'string' || !FECHA_RE.test(fecha)) return false;
    const [anio, mes, dia] = fecha.split('-').map(Number);
    const d = new Date(Date.UTC(anio, mes - 1, dia));
    return d.getUTCFullYear() === anio && d.getUTCMonth() === mes - 1 && d.getUTCDate() === dia;
}

/**
 * @typedef {{ ok: true, feriados: Array<{fecha:string,descripcion:string,created_at:string}> } | {
 *   ok: false, kind: 'error', mensaje: string
 * }} LoadFeriadosResult
 */

/**
 * Lista completa de feriados (spec: la app no pagina por mes — el calendario
 * filtra client-side vía `feriados-ui-policy.js#feriadosDelMes`).
 * @returns {Promise<LoadFeriadosResult>}
 */
export async function loadFeriados() {
    try {
        const feriados = await listFeriados();
        return { ok: true, feriados };
    } catch (err) {
        window.Sentry?.captureException(err);
        return { ok: false, kind: 'error', mensaje: ERROR_MESSAGES.error_interno };
    }
}

/**
 * @typedef {{ ok: true, feriado: {fecha:string,descripcion:string,created_at:string} } | {
 *   ok: false, kind: 'validacion'|'error', mensaje: string
 * }} FeriadoMutacionResult
 */

/**
 * Alta de un feriado (spec "Adding a feriado": click un día, ingresar
 * "Descripción del feriado", confirmar). Valida ANTES de tocar el
 * repositorio — mismo criterio que `admin-payroll-config.service.js`.
 * @param {{ fecha: string, descripcion: string }} params
 * @returns {Promise<FeriadoMutacionResult>}
 */
export async function crearFeriado({ fecha, descripcion } = {}) {
    if (!esFechaValida(fecha)) {
        return { ok: false, kind: 'validacion', mensaje: ERROR_MESSAGES.fecha_invalida };
    }
    const descripcionTrim = typeof descripcion === 'string' ? descripcion.trim() : '';
    if (!descripcionTrim) {
        return { ok: false, kind: 'validacion', mensaje: ERROR_MESSAGES.descripcion_requerida };
    }

    try {
        const feriado = await createFeriado({ fecha, descripcion: descripcionTrim });
        return { ok: true, feriado };
    } catch (err) {
        if (err?.code === DUPLICATE_KEY) {
            return { ok: false, kind: 'validacion', mensaje: ERROR_MESSAGES.ya_existe };
        }
        window.Sentry?.captureException(err);
        return { ok: false, kind: 'error', mensaje: ERROR_MESSAGES.error_interno };
    }
}

/**
 * Baja de un feriado (spec "Removing a feriado": "Quitar" en un día marcado).
 * @param {string} fecha
 * @returns {Promise<{ok:true}|{ok:false,kind:'error',mensaje:string}>}
 */
export async function eliminarFeriado(fecha) {
    try {
        await deleteFeriado(fecha);
        return { ok: true };
    } catch (err) {
        window.Sentry?.captureException(err);
        return { ok: false, kind: 'error', mensaje: ERROR_MESSAGES.error_interno };
    }
}
